/**
 * Server-side data fetchers for wallet-related data
 * These functions are designed to be used in Server Components for prefetching
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import fetchAPI from '@/lib/fetch-api';
import type { Wallet } from '@/types/wallet';

interface BankDetail {
  id: number;
  is_external: number;
  iban: string;
  primary: number;
  address: string | null;
  name: string | null;
  account_number: string;
  swift: string | null;
  holder: string;
  holder_city: string | null;
  holder_address: string | null;
  holder_zip_code: string | null;
  phone: string | null;
  identifying_number: string | null;
  aba_routing_number: string | null;
  currency_id: number;
}

interface WalletBalance {
  id: number;
  user_id: number;
  available_balance: string;
  current_balance: string;
  reserved_balance: string;
  reference_number: string;
  currency_id: number;
  bank_details: BankDetail[];
  pending_balance_rewards_total: string;
  balance_rewards_enabled: boolean;
}

interface FetchWalletsOptions {
  userId?: number;
  walletType?: 'default' | 'pre_payment' | 'organisation' | 'entity' | 'salary';
}

/**
 * Fetches all wallets for the authenticated user
 * Server-side fetcher that uses httpOnly cookies for authentication
 * @param {FetchWalletsOptions} options - Optional filtering parameters
 * @returns {Promise<Wallet[]>} Array of wallet records
 * @throws {Error} If the API request fails
 */
export async function fetchWallets(options?: FetchWalletsOptions): Promise<Wallet[]> {
  const params = new URLSearchParams();

  if (options?.userId) {
    params.append('user_id', options.userId.toString());
  }
  if (options?.walletType) {
    params.append('wallet_type', options.walletType);
  }

  const queryString = params.toString();
  const url = queryString ? `/wallets?${queryString}` : '/wallets';

  const response = await fetchAPI<Wallet[]>(url, {
    method: 'GET',
    cache: 'no-store',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch wallets');
  }

  return response.data ?? [];
}

/**
 * Fetches wallet balance for a specific currency
 * Server-side fetcher that uses httpOnly cookies for authentication
 * @param {number} currencyId - The currency ID to fetch the wallet balance for
 * @returns {Promise<WalletBalance | null>} The wallet balance data or null on error
 */
export async function fetchWalletBalance(currencyId: number): Promise<WalletBalance | null> {
  try {
    const response = await fetchAPI<WalletBalance>(`/wallets/balance?currency_id=${currencyId}`, {
      method: 'GET',
      cache: 'no-store',
    });

    if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
      console.error('[fetchWalletBalance] Failed:', response.error || response.message);
      return null;
    }

    return response.data! || null;
  } catch (error) {
    console.error('[fetchWalletBalance] Error:', error);
    return null;
  }
}

export interface DedicatedIbanAvailableData {
  available: boolean;
}

export interface IbanDetailsData {
  wallet_id: number;
  status: 'new' | 'pending' | 'active' | 'declined';
  holder: string;
  iban: string;
  swift_bic: string;
  is_dedicated: boolean;
  requested_at: string;
  activated_at: string | null;
}

/** Entity from GET /wallet-iban/entities (Wallet API) */
export interface WalletIbanEntity {
  id: number;
  wallet_id: number;
  name: string;
  status: string;
}

/**
 * Fetches entities for the entity wallet dropdown (GET /wallet-iban/entities, no query params)
 * Server-side fetcher that uses httpOnly cookies for authentication
 * @returns {Promise<WalletIbanEntity[]>} Array of entities or empty array on error
 */
export async function fetchWalletIbanEntities(): Promise<WalletIbanEntity[]> {
  try {
    const response = await fetchAPI<{ data: WalletIbanEntity[] }>('/wallet-iban/entities', {
      method: 'GET',
      cache: 'no-store',
    });

    if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
      console.error('[fetchWalletIbanEntities] Failed:', response.error || response.message);
      return [];
    }

    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('[fetchWalletIbanEntities] Error:', error);
    return [];
  }
}

/**
 * Checks if a dedicated IBAN is available for an entity
 * Server-side fetcher that uses httpOnly cookies for authentication
 * @param {number} entityId - The entity ID
 * @returns {Promise<DedicatedIbanAvailableData | null>} The availability data or null on error
 */
export async function fetchDedicatedIbanAvailable(
  entityId: number
): Promise<DedicatedIbanAvailableData | null> {
  try {
    const response = await fetchAPI<DedicatedIbanAvailableData>(
      `/wallet-iban/${entityId}/dedicated-available`,
      {
        method: 'GET',
        cache: 'no-store',
      }
    );

    if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
      console.error('[fetchDedicatedIbanAvailable] Failed:', response.error || response.message);
      return null;
    }

    return response.data! || null;
  } catch (error) {
    console.error('[fetchDedicatedIbanAvailable] Error:', error);
    return null;
  }
}

/**
 * Fetches dedicated IBAN status for an entity
 * Server-side fetcher that uses httpOnly cookies for authentication
 * @param {number} entityId - The entity ID
 * @returns {Promise<IbanDetailsData | null>} The IBAN details or null on error
 */
export async function fetchDedicatedIbanStatus(entityId: number): Promise<IbanDetailsData | null> {
  try {
    const response = await fetchAPI<IbanDetailsData>(`/wallet-iban/${entityId}`, {
      method: 'GET',
      cache: 'no-store',
    });

    if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
      // 404 is expected if IBAN doesn't exist yet, so don't log as error
      if (response.status === 404) {
        return null;
      }
      console.error('[fetchDedicatedIbanStatus] Failed:', response.error || response.message);
      return null;
    }

    return response.data! || null;
  } catch (error) {
    console.error('[fetchDedicatedIbanStatus] Error:', error);
    return null;
  }
}
